// The booking form's client picker (PMS-CARDEX-BRIEF §6.1 S6).
//
// A SEARCH, not a list. It replaced a `<select>` of every `pms_guest` row, which
// PostgREST cut at 1 000 rows once the cardex held 5 343 — the desk saw A to
// roughly D and nobody after — and which would have been unusable at full
// length anyway. Two characters start a search; the database matches every word
// against name, e-mail, telephone, town and Vega number.

import { useEffect, useRef, useState } from 'react';
import { PMS_CUSTOMER_TYPE_LABELS_FR, type GuestService, type GuestSummary } from '@lemontaigu/pms-core';

export interface GuestPickerProps {
  guests: Pick<GuestService, 'search'>;
  onPick: (guest: GuestSummary) => void;
  /** Wait before asking — injected for tests. */
  debounceMs?: number;
}

const LIMIT = 20;

export function GuestPicker({ guests, onPick, debounceMs = 250 }: GuestPickerProps) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<GuestSummary[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  // A slow answer to an older query must not replace a newer one's.
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setSearched(false);
      setError('');
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      void guests.search({ query: q, limit: LIMIT }).then((r) => {
        if (mine !== seq.current) return;
        setSearched(true);
        if (!r.ok) {
          setError(r.error.message);
          setHits([]);
          return;
        }
        setError('');
        setHits(r.value.hits);
        setTruncated(r.value.truncated);
      });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, guests, debounceMs]);

  const pick = (g: GuestSummary) => {
    onPick(g);
    setQuery('');
  };

  return (
    <div className="pms-picker">
      <input
        type="search"
        value={query}
        placeholder="Nom, e-mail, téléphone ou n° Vega"
        aria-label="Rechercher un client"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setQuery('');
        }}
      />
      {error && <p className="pms-warn">⚠ {error}</p>}
      {searched && !error && (
        <ul className="pms-picker-list" role="listbox" aria-label="Clients trouvés">
          {hits.map((g) => (
            <li key={g.id}>
              <button type="button" role="option" aria-selected={false} onClick={() => pick(g)}>
                <strong>{g.lastName.toUpperCase()}</strong> {g.firstName}
                <span className="pms-sub">
                  {' · '}
                  {[
                    g.customerType === 'societe' ? PMS_CUSTOMER_TYPE_LABELS_FR.societe : null,
                    g.email,
                    g.mobile ?? g.phone,
                    g.city,
                    g.sourceVegaRef && `Vega ${g.sourceVegaRef}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
          {hits.length === 0 && <li className="pms-empty">Aucun client — saisissez-le ci-dessous.</li>}
          {truncated && <li className="pms-hint">Plus de {LIMIT} clients — précisez la recherche.</li>}
        </ul>
      )}
    </div>
  );
}
